import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'api-service';
import { handleChangeSortBy } from 'store/helpers/filterHelpers';
import {
  instagramCommentInitialState,
  instagramInitialState,
  newsInitialState,
  newspaperInitialState,
  publicInitialState,
  telegramInitialState,
  twitterInitialState,
} from './initialState';
import {
  applyWebsiteSettingsToOtherReducers,
  createFiltersWithAllFields,
  createFiltersWithOnlySelectedFields,
} from './settingsHelpers';

export const filtersInitialState = {
  public: publicInitialState,
  news: newsInitialState,
  instagram: instagramInitialState,
  instagramComment: instagramCommentInitialState,
  telegram: telegramInitialState,
  twitter: twitterInitialState,
  newspaper: newspaperInitialState,
};

const sortInitialState = {
  selected: 'time',
  order: 'desc',
};

export const sortsInitialState = {
  news: sortInitialState,
  instagram: sortInitialState,
  instagramComment: sortInitialState,
  telegram: sortInitialState,
  twitter: sortInitialState,
  newspaper: sortInitialState,
};

export const settingsInitialState = {
  updateWebsiteSettings: {
    loading: false,
    error: null,
  },
  fetchWebsiteSettings: {
    loading: false,
    success: false,
    error: null,
  },
  isWebsiteSettingsSynced: false,
  filters: filtersInitialState,
  sortBy: sortsInitialState,
};

export const updateWebsiteSettings = createAsyncThunk(
  'settingsReducer/updateWebsiteSettings',
  async ({ shouldApplyToOtherReducers }, thunkAPI) => {
    const { filters, sortBy } = thunkAPI.getState().settingsReducer;
    const updatedSettings = {};
    for (const platform in filters)
      updatedSettings[platform] = createFiltersWithOnlySelectedFields({
        currentPlatformFilters: filters[platform],
      });
    try {
      await apiPost('/user/user-settings', {
        key: 'user_settings',
        value: {
          filters: updatedSettings,
          sorts: sortBy,
        },
      });
      if (shouldApplyToOtherReducers) {
        applyWebsiteSettingsToOtherReducers({
          filters,
          sorts: sortBy,
          thunkAPI,
        });
      }
      return { isSuccessful: true };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchWebsiteSettings = createAsyncThunk(
  'settingsReducer/fetchWebsiteSettings',
  async ({ shouldApplyToOtherReducers }, thunkAPI) => {
    try {
      const response = await apiGet('/user/user-settings', {
        key: 'user_settings',
      });
      const responseFilters = response.data.result.data.value?.filters;
      const responseSorts = response.data.result.data.value?.sorts;
      const allPlatformsFilters = thunkAPI.getState().settingsReducer.filters;
      if (!responseSorts || !responseFilters) {
        if (shouldApplyToOtherReducers)
          applyWebsiteSettingsToOtherReducers({
            filters: filtersInitialState,
            sorts: sortsInitialState,
            thunkAPI,
          });
        return {
          filters: filtersInitialState,
          sorts: sortsInitialState,
        };
      }
      if (shouldApplyToOtherReducers) {
        applyWebsiteSettingsToOtherReducers({
          filters: responseFilters,
          sorts: responseSorts,
          thunkAPI,
        });
      }
      for (const platform in allPlatformsFilters) {
        createFiltersWithAllFields({
          allPlatformsFilters,
          responseFilters,
          platform,
        });
      }
      return {
        filters: responseFilters,
        sorts: responseSorts,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const settingsSlice = createSlice({
  name: 'settingsReducer',
  initialState: settingsInitialState,
  reducers: {
    setSelectedItems: (state, action) => {
      const { componentName, item, isChecked, parentPlatform } = action.payload;
      if (isChecked) {
        state.filters[parentPlatform][componentName].selectedItems[item.id] =
          item;
      } else {
        delete state.filters[parentPlatform][componentName].selectedItems[
          item.id
        ];
      }
    },
    setSelectedFilterValue: (state, action) => {
      const { value, componentName, parentPlatform } = action.payload;
      state.filters[parentPlatform][componentName].selectedValue = value;
    },
    setAdvertFilterValues: (state, action) => {
      const { active, min, max, componentName, parentPlatform } =
        action.payload;
      state.filters[parentPlatform][componentName].selectedObjectValues = {
        min,
        max,
        active,
      };
    },
    changeSortBy: (state, action) => {
      handleChangeSortBy(state, action);
    },
    resetFilter: (state, action) => {
      const { componentName, parentPlatform } = action.payload;
      state.filters[parentPlatform][componentName] =
        filtersInitialState[parentPlatform][componentName];
    },
    setDefaultCurrentDateSettings: (state, action) => {
      const {
        selectedTimeDuration,
        timeDuration,
        fromDate,
        toDate,
        hasDefaultDateTime,
      } = action.payload;
      const { defaultDateTimeSelectBox } = state.filters.public;
      defaultDateTimeSelectBox.selectedTimeDuration = selectedTimeDuration;
      defaultDateTimeSelectBox.timeDuration = timeDuration;
      defaultDateTimeSelectBox.fromDate = fromDate;
      defaultDateTimeSelectBox.toDate = toDate;
      defaultDateTimeSelectBox.hasDefaultDateTime = hasDefaultDateTime;
    },
    restoreWebsiteDefaultSettings: (state) => {
      state.filters = filtersInitialState;
      state.sortBy = sortsInitialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateWebsiteSettings.fulfilled, (state, action) => {
        state.updateWebsiteSettings.loading = false;
        state.updateWebsiteSettings.error = null;
        const { shouldApplyToOtherReducers } = action.meta.arg;
        if (shouldApplyToOtherReducers) {
          state.isWebsiteSettingsSynced = true;
        }
      })
      .addCase(updateWebsiteSettings.pending, (state) => {
        state.updateWebsiteSettings.loading = true;
        state.updateWebsiteSettings.error = null;
      })
      .addCase(updateWebsiteSettings.rejected, (state, action) => {
        state.updateWebsiteSettings.loading = false;
        state.updateWebsiteSettings.error = action.payload;
      })
      .addCase(fetchWebsiteSettings.fulfilled, (state, action) => {
        state.filters = action.payload.filters;
        state.sortBy = action.payload.sorts;
        state.fetchWebsiteSettings.loading = false;
        state.fetchWebsiteSettings.success = true;
        state.fetchWebsiteSettings.error = null;
        const { shouldApplyToOtherReducers } = action.meta.arg;
        if (shouldApplyToOtherReducers) {
          state.isWebsiteSettingsSynced = true;
        }
      })
      .addCase(fetchWebsiteSettings.pending, (state) => {
        state.fetchWebsiteSettings.loading = true;
        state.updateWebsiteSettings.error = null;
      })
      .addCase(fetchWebsiteSettings.rejected, (state, action) => {
        state.fetchWebsiteSettings.loading = false;
        state.fetchWebsiteSettings.error = action.payload;
      });
  },
});

export const {
  setSelectedItems,
  setSelectedFilterValue,
  setAdvertFilterValues,
  changeSortBy,
  resetFilter,
  setDefaultCurrentDateSettings,
  restoreWebsiteDefaultSettings,
} = settingsSlice.actions;
